import React, { useContext, useMemo, useState } from 'react'
import API from '../../services/API'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded'
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded'
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded'
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import IconButton from '@mui/material/IconButton'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded'
import { GlobalContext } from '../../App'
import ErrorModal from '../../components/Modals/Error'

// The four ticket types. `clickUpType` is what the server keys on; the
// clinical type is filed in ClickUp as a Bug with Severity = Clinical Risk and
// also written to the DCB0129 Safety Incident Management Log in the CRM.
export const TICKET_CONFIGS = {
  support: {
    label: 'General enquiry',
    shortLabel: 'Enquiry',
    clickUpType: 'Support Request',
    icon: HeadsetMicRoundedIcon,
    color: '#239AA4',
    bg: 'rgba(35,154,164,0.12)',
    tileTitle: 'General enquiry',
    tileText: 'A question about AmbuNet, your account, or how to do something.',
    intro:
      'Ask us anything about AmbuNet and we will point you in the right direction.',
    subjectLabel: 'What do you need help with?',
    detailsLabel: 'How can we help?',
    detailsPlaceholder:
      'Please include as much detail as possible so we can respond effectively.',
    extraLabel: 'Anything you have already tried?',
    extraPlaceholder:
      'Example: I checked the documentation, refreshed the page, or tried another browser.',
    submitLabel: 'Submit enquiry',
  },
  bug: {
    label: 'Bug report',
    shortLabel: 'Bug',
    clickUpType: 'Bug',
    icon: BugReportRoundedIcon,
    color: '#D4145A',
    bg: 'rgba(212,20,90,0.10)',
    tileTitle: 'Report a bug',
    tileText: 'Something is not working as expected.',
    intro:
      'Tell us what went wrong so we can investigate and fix the issue as quickly as possible.',
    subjectLabel: 'Brief summary of the problem',
    detailsLabel: 'What happened?',
    detailsPlaceholder:
      'Describe what you were doing, what went wrong, and what you expected to happen.',
    extraLabel: 'Steps to reproduce',
    extraPlaceholder:
      'Example: Open ePCR → Add medication → Save record → Error appears',
    submitLabel: 'Submit bug report',
  },
  feature: {
    label: 'Feature request',
    shortLabel: 'Feature',
    clickUpType: 'Feature Request',
    icon: TipsAndUpdatesRoundedIcon,
    color: '#16A34A',
    bg: 'rgba(22,163,74,0.10)',
    tileTitle: 'Request a feature',
    tileText: 'Suggest an improvement or a new workflow.',
    intro:
      'Share an idea or improvement that would make AmbuNet better for your organisation.',
    subjectLabel: 'Feature idea summary',
    detailsLabel: 'What would you like AmbuNet to do?',
    detailsPlaceholder:
      'Describe the feature, workflow, or improvement you would like to see.',
    extraLabel: 'Why would this be useful?',
    extraPlaceholder:
      'Tell us who would benefit and what problem this would solve.',
    submitLabel: 'Submit feature request',
  },
  clinical: {
    label: 'Clinical safety incident',
    shortLabel: 'Clinical safety',
    clickUpType: 'Clinical Safety Incident',
    icon: HealthAndSafetyRoundedIcon,
    color: '#B42318',
    bg: 'rgba(180,35,24,0.10)',
    tileTitle: 'Clinical safety incident',
    tileText:
      'AmbuNet could have, or did, contribute to patient harm, a near miss, or a lost or wrong clinical record.',
    intro:
      'This goes straight to the AmbuNet Clinical Safety Officer and is recorded in our safety incident log.',
    subjectLabel: 'What happened, in one line',
    detailsLabel: 'Describe what happened',
    detailsPlaceholder:
      'What you were doing, what the system did, what you expected, and what effect it had on the patient or the record. Do not include patient names or identifiers.',
    extraLabel: 'Immediate actions taken',
    extraPlaceholder:
      'Example: re-checked the record on paper, informed the clinical lead, used a workaround.',
    submitLabel: 'Report clinical safety incident',
  },
}

export const TICKET_TYPE_ORDER = ['support', 'bug', 'feature', 'clinical']

const modules = [
  'Clinical Records',
  'Event Planning',
  'Fleet & Equipment',
  'Governance',
  'HR & Employees',
  'Command & Control',
  'Patient Transport',
  'Billing',
  'Mobile App',
  'Other',
]

const impacts = [
  'Blocking work',
  'Major issue',
  'Minor issue',
  'General enquiry',
  'Suggestion',
]

// NHS patient-safety harm grades plus "near miss" for events that did not
// reach a patient. The Clinical Safety Officer re-grades at triage.
const harmOptions = [
  {
    value: 'Near miss',
    label: 'Near miss - could have caused harm but did not reach a patient',
  },
  { value: 'No harm', label: 'No harm - reached a patient, no harm caused' },
  { value: 'Low harm', label: 'Low harm - minimal harm, minor treatment' },
  {
    value: 'Moderate harm',
    label: 'Moderate harm - further treatment or a longer stay',
  },
  { value: 'Severe harm', label: 'Severe harm - permanent or long-term harm' },
  { value: 'Death', label: 'Death' },
  { value: 'Unknown', label: 'Not sure yet' },
]

const toDateTimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default function SupportRequestForm({
  type = 'choose',
  onClose,
  adminMode = false,
  tenantId,
  userId,
  userName,
}) {
  const [requestType, setRequestType] = useState(
    TICKET_CONFIGS[type] ? type : null,
  )
  const config = requestType ? TICKET_CONFIGS[requestType] : null
  const isClinical = requestType === 'clinical'
  const { setToast } = useContext(GlobalContext)

  const [form, setForm] = useState({
    subject: '',
    details: '',
    extra: '',
    module: '',
    impact: '',
    source: 'Phone',
    sendEmail: true,
    // Clinical safety incident fields
    occurredAt: toDateTimeLocal(new Date()),
    harm: '',
    isOngoing: '',
    patientsAffected: '',
    recordReferences: '',
    phone: '',
  })
  const [attachments, setAttachments] = useState([])

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const canSubmit = useMemo(() => {
    if (!config || submitting) return false
    if (!form.subject.trim() || !form.details.trim()) return false
    if (isClinical) {
      return !!form.module && !!form.harm && !!form.isOngoing
    }
    return true
  }, [config, form, isClinical, submitting])

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const MAX_FILES = 5
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

  const handleAttachments = (e) => {
    const selected = Array.from(e.target.files || [])

    const validFiles = selected.filter((file) => {
      if (file.size > MAX_FILE_SIZE && !adminMode) {
        setToast({
          message: `${file.name} is too large. Maximum file size is 10MB.`,
          severity: 'error',
        })
        return false
      }
      return true
    })

    setAttachments((prev) => [...prev, ...validFiles].slice(0, MAX_FILES))
    e.target.value = ''
  }

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB'
    const kb = bytes / 1024
    if (kb < 1024) return `${Math.round(kb)} KB`
    return `${(kb / 1024).toFixed(1)} MB`
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setSubmitError(false)

    try {
      const payload = new FormData()

      payload.append('type', config.clickUpType)
      payload.append('subject', form.subject.trim())
      payload.append(
        'details',
        `${form.details.trim()}\n\nVersion: ${process.env.AMBUNET_APP_VERSION}-${process.env.AMBUNET_BUILD_NUMBER}`,
      )
      payload.append('extra', form.extra.trim())
      payload.append('module', form.module)
      payload.append('impact', isClinical ? 'Clinical safety' : form.impact)
      payload.append('platform', 'Web')

      if (isClinical) {
        payload.append('occurredAt', form.occurredAt)
        payload.append('harm', form.harm)
        payload.append('isOngoing', form.isOngoing)
        payload.append('patientsAffected', form.patientsAffected)
        payload.append('recordReferences', form.recordReferences.trim())
        payload.append('phone', form.phone.trim())
        payload.append('immediateActions', form.extra.trim())
      }

      if (adminMode) {
        payload.append('source', form.source)
        payload.append('sendEmail', form.sendEmail ? 'true' : 'false')
      }

      attachments.forEach((file) => {
        payload.append('attachments', file)
      })

      const url = adminMode
        ? `/admincrm/tenant/${tenantId}/users/${userId}/support-request`
        : '/dev/support-request'

      const res = await API.post(url, payload, {
        headers: {
          'Content-Type': undefined,
        },
      })

      if (res.status === 200) {
        if (!adminMode) {
          const ref = res.data?.ticketRef ? ` Reference ${res.data.ticketRef}.` : ''
          setToast({
            message: isClinical
              ? `Clinical safety incident reported and logged.${ref} The Clinical Safety Officer has been notified.`
              : `${config.label} submitted. Thank you!${ref}`,
          })
        }
        onClose?.(res.data)
      } else {
        setSubmitError(true)
      }
    } catch (error) {
      console.error(error)
      setSubmitError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const headerColor = config?.color || '#239AA4'
  const HeaderIcon = config?.icon || HeadsetMicRoundedIcon

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 24px 80px rgba(15,23,42,0.18)',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          background: isClinical
            ? 'radial-gradient(circle at top left, rgba(180,35,24,0.34), transparent 34%), linear-gradient(135deg, #1A2230 0%, #242F40 55%, #101827 100%)'
            : 'radial-gradient(circle at top left, rgba(35,154,164,0.24), transparent 34%), linear-gradient(135deg, #1A2230 0%, #242F40 55%, #101827 100%)',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.16)',
              color: headerColor,
              flexShrink: 0,
            }}
          >
            <HeaderIcon />
          </Box>

          <Box>
            <Chip
              label={config ? config.label : 'Raise a ticket'}
              size="small"
              sx={{
                mb: 1,
                color: 'white',
                fontWeight: 800,
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.18)',
              }}
            />

            <Typography variant="h5" fontWeight={900} color="white">
              {config ? config.label : 'What is this ticket about?'}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.68)', mt: 0.5 }}
            >
              {config
                ? config.intro
                : 'Pick the option that best describes it so it reaches the right people straight away.'}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Box
        sx={{
          height: 4,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: submitting ? 'transparent' : 'rgba(15,23,42,0.06)',
        }}
      >
        {submitting && <LinearProgress sx={{ height: 4 }} color="contrast" />}
      </Box>

      {!config ? (
        <Box sx={{ p: { xs: 2.5, md: 3 }, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={1.5}>
            {TICKET_TYPE_ORDER.map((key) => (
              <Grid item xs={12} sm={6} key={key} sx={{ display: 'flex' }}>
                <TypeTile
                  config={TICKET_CONFIGS[key]}
                  onClick={() => setRequestType(key)}
                />
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Button
              onClick={() => onClose?.()}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2.5, md: 3 },
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <Stack spacing={2.2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {TICKET_TYPE_ORDER.map((key) => {
                const option = TICKET_CONFIGS[key]
                const OptionIcon = option.icon
                const selected = requestType === key
                return (
                  <Chip
                    key={key}
                    icon={<OptionIcon />}
                    label={option.label}
                    onClick={() => setRequestType(key)}
                    disabled={submitting}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 800,
                      ...(selected
                        ? {
                            backgroundColor: option.color,
                            color: 'white',
                            '& .MuiChip-icon': { color: 'white' },
                            '&:hover': { backgroundColor: option.color },
                          }
                        : {
                            borderColor: option.color,
                            color: option.color,
                            '& .MuiChip-icon': { color: option.color },
                          }),
                    }}
                  />
                )
              })}
            </Stack>

            {isClinical ? (
              <>
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  <strong>If a patient is at immediate risk, follow your
                  organisation&apos;s clinical escalation first.</strong> This
                  form reaches the AmbuNet Clinical Safety Officer; it is not
                  an emergency service.
                </Alert>
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Do not include patient names, dates of birth, NHS numbers or
                  other identifiers. Use record references instead (ePCR
                  number, booking or CAD reference).
                </Alert>
              </>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                AmbuNet support is for software support only. For shift
                queries, operational matters, patient care concerns, or
                clinical advice, contact your organisation directly.
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label={config.subjectLabel}
              value={form.subject}
              onChange={(e) => updateField('subject', e.target.value)}
              autoComplete="off"
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth required={isClinical}>
                <InputLabel>Related module</InputLabel>
                <Select
                  label="Related module"
                  value={form.module}
                  onChange={(e) => updateField('module', e.target.value)}
                >
                  {modules.map((module) => (
                    <MenuItem key={module} value={module}>
                      {module}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isClinical ? (
                <TextField
                  fullWidth
                  required
                  type="datetime-local"
                  label="When did it happen?"
                  value={form.occurredAt}
                  onChange={(e) => updateField('occurredAt', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : requestType === 'bug' && (
                <FormControl fullWidth>
                  <InputLabel>Impact</InputLabel>
                  <Select
                    label="Impact"
                    value={form.impact}
                    onChange={(e) => updateField('impact', e.target.value)}
                  >
                    {impacts.map((impact) => (
                      <MenuItem key={impact} value={impact}>
                        {impact}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label={config.detailsLabel}
              placeholder={config.detailsPlaceholder}
              value={form.details}
              onChange={(e) => updateField('details', e.target.value)}
            />

            {isClinical && (
              <>
                <FormControl fullWidth required>
                  <InputLabel>Effect on the patient</InputLabel>
                  <Select
                    label="Effect on the patient"
                    value={form.harm}
                    onChange={(e) => updateField('harm', e.target.value)}
                  >
                    {harmOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth required>
                    <InputLabel>Is it still happening?</InputLabel>
                    <Select
                      label="Is it still happening?"
                      value={form.isOngoing}
                      onChange={(e) => updateField('isOngoing', e.target.value)}
                    >
                      <MenuItem value="Yes">Yes, it is still happening</MenuItem>
                      <MenuItem value="No">No, it has stopped or was a one-off</MenuItem>
                      <MenuItem value="Unknown">Not sure</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    label="Patients affected (approx.)"
                    value={form.patientsAffected}
                    onChange={(e) => updateField('patientsAffected', e.target.value)}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Record references"
                    placeholder="ePCR number, booking or CAD reference"
                    helperText="References only, no patient identifiers"
                    value={form.recordReferences}
                    onChange={(e) => updateField('recordReferences', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Best number to call you on"
                    placeholder="Optional"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                  />
                </Stack>
              </>
            )}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label={config.extraLabel}
              placeholder={config.extraPlaceholder}
              value={form.extra}
              onChange={(e) => updateField('extra', e.target.value)}
            />

            {adminMode && (
              <Stack spacing={2}>
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  You are creating this request on behalf of{' '}
                  {userName || 'this user'}.
                </Alert>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Request source</InputLabel>
                    <Select
                      label="Request source"
                      value={form.source}
                      onChange={(e) => updateField('source', e.target.value)}
                    >
                      {[
                        'Phone',
                        'Email',
                        'Text message',
                        'Teams',
                        'Live chat',
                        'Other',
                      ].map((source) => (
                        <MenuItem key={source} value={source}>
                          {source}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.sendEmail}
                        onChange={(e) =>
                          updateField('sendEmail', e.target.checked)
                        }
                      />
                    }
                    label="Send confirmation email"
                  />
                </Stack>
              </Stack>
            )}

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px dashed rgba(35,154,164,0.45)',
                bgcolor: 'rgba(35,154,164,0.04)',
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography fontWeight={900}>Attachments</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add screenshots, PDFs, or supporting files. Maximum 5
                      files, 10MB each.
                      {isClinical ? ' Blur or crop out patient details.' : ''}
                    </Typography>
                  </Box>

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AttachFileRoundedIcon />}
                    disabled={submitting || attachments.length >= MAX_FILES}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    Add files
                    <input
                      hidden
                      multiple
                      type="file"
                      onChange={handleAttachments}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    />
                  </Button>
                </Stack>

                {attachments.length > 0 && (
                  <Stack spacing={1}>
                    {attachments.map((file, index) => (
                      <Paper
                        key={`${file.name}-${index}`}
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: '1px solid rgba(15,23,42,0.08)',
                          bgcolor: '#fff',
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <InsertDriveFileRoundedIcon sx={{ color: '#239AA4' }} />

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={800} noWrap>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={() => removeAttachment(index)}
                            disabled={submitting}
                          >
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>

            <Divider />

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              justifyContent="flex-end"
            >
              <Button
                onClick={() => onClose?.()}
                disabled={submitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2,
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!canSubmit}
                endIcon={<SendRoundedIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                  borderRadius: 2,
                  px: 3,
                  backgroundColor: isClinical ? '#B42318' : '#239AA4',
                  '&:hover': {
                    backgroundColor: isClinical ? '#8F1C13' : '#1D858E',
                  },
                }}
              >
                {config.submitLabel}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <ErrorModal
        open={submitError}
        onClose={() => setSubmitError(false)}
        message="We were unable to submit your request. Please email support@ambunet.co.uk"
      />
    </Paper>
  )
}

function TypeTile({ config, onClick }) {
  const Icon = config.icon
  return (
    <Button
      fullWidth
      onClick={onClick}
      sx={{
        p: 0,
        flex: 1,
        display: 'flex',
        alignItems: 'stretch',
        textTransform: 'none',
        color: 'inherit',
        borderRadius: 3,
        '&:hover': { backgroundColor: 'transparent' },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minWidth: 0,
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(15,23,42,0.08)',
          background: '#fff',
          transition: 'all 0.18s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: config.color,
            boxShadow: '0 14px 32px rgba(15,23,42,0.10)',
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: config.bg,
              color: config.color,
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography fontWeight={900}>{config.tileTitle}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
              {config.tileText}
            </Typography>
          </Box>
          <ArrowForwardRoundedIcon sx={{ color: config.color, mt: 0.5 }} />
        </Stack>
      </Paper>
    </Button>
  )
}
